import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  QrCode2 as QrIcon,
  OpenInNew as OpenIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { getFairUrl } from '../../jobBoard/hostSlug';
import AtsFairQr from './AtsFairQr';
import AtsDeleteFairDialog from './AtsDeleteFairDialog';
import { formatDate } from './fairHelpers';

/**
 * Ferias (PLAN_FERIAS): agrupaciones de vacantes con su propia página pública
 * (/feria/:slug) y QR, por ejemplo para una feria laboral. Las ve cualquier
 * usuario del ATS de la organización; editar y eliminar solo quien la creó o
 * un admin (canEdit lo calcula el backend).
 */
const AtsFairsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [fairs, setFairs] = useState([]);
  const [boardSlug, setBoardSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrTarget, setQrTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadFairs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/organizations/job-fairs', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = response.data.response;
      if (data?.status_response === 'OK') {
        setFairs(data.data || []);
        setBoardSlug(data.boardSlug || null);
      } else {
        setError(t('ats.fairs.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('ats.fairs.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) loadFairs();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const handleCopy = async (fair) => {
    try {
      await navigator.clipboard.writeText(getFairUrl(fair.slug, boardSlug));
      showAlert(t('ats.fairs.linkCopied'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.fairs.linkCopyError'), 'error');
    }
  };

  const purpleButton = { backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('ats.fairs.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('ats.fairs.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/ats/fairs/new')}
          sx={purpleButton}
        >
          {t('ats.fairs.newFair')}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && fairs.length === 0 && (
        <Alert severity="info">{t('ats.fairs.empty')}</Alert>
      )}

      {!loading && !error && fairs.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.fairs.colName')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.fairs.colPositions')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.fairs.colCreatedBy')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ats.fairs.colUpdated')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.fairs.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fairs.map((fair) => (
                <TableRow
                  key={fair.uid}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/ats/fairs/${fair.uid}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{fair.name}</Typography>
                    <Typography variant="caption" color="text.secondary">/feria/{fair.slug}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        {t('ats.fairs.positionsShown', { shown: fair.positionsShown, total: fair.positionsTotal })}
                      </Typography>
                      {fair.positionsShown === 0 && (
                        <Chip size="small" color="warning" variant="outlined" label={t('ats.fairs.noneShown')} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{fair.createdByName || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(fair.updatedAt)}</Typography>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={t('ats.fairs.copyLink')}>
                      <IconButton size="small" onClick={() => handleCopy(fair)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('ats.fairs.showQr')}>
                      <IconButton size="small" onClick={() => setQrTarget(fair)}>
                        <QrIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('ats.fairs.openPage')}>
                      <IconButton
                        size="small"
                        component="a"
                        href={getFairUrl(fair.slug, boardSlug)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={fair.canEdit ? t('ats.fairs.edit') : t('ats.fairs.view')}>
                      <IconButton size="small" onClick={() => navigate(`/ats/fairs/${fair.uid}`)}>
                        {fair.canEdit ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {fair.canEdit && (
                      <Tooltip title={t('ats.fairs.delete')}>
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(fair)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(qrTarget)} onClose={() => setQrTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{qrTarget?.name}</DialogTitle>
        <DialogContent>
          {qrTarget && <AtsFairQr url={getFairUrl(qrTarget.slug, boardSlug)} slug={qrTarget.slug} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrTarget(null)} color="inherit">
            {t('ats.fairs.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <AtsDeleteFairDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        fairId={deleteTarget?.uid}
        fairName={deleteTarget?.name}
        onDeleted={(id) => setFairs((prev) => prev.filter((f) => f.uid !== id))}
      />
    </Box>
  );
};

export default AtsFairsLayout;
